import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Drives the bottom-pin correction (see #pinToBottomIfWasAtBottom) on
   * every frame for a short window after the header's height changes while
   * #wasAtBottom. That height change can also trigger further, unrelated
   * height changes elsewhere on the page (e.g. hero.liquid sizes its hero
   * section off the same --header-group-height custom property this
   * header's own height feeds, specifically to keep header+hero height
   * constant as the header collapses/expands) that this class has no
   * direct way to observe -- rather than tracking down every element that
   * might compensate like that on every page template, polling scrollTop
   * back into place each frame catches all of them the moment any of them
   * actually change the scroll container's height, whatever page-specific
   * mechanism is behind it.
   * @type {number | null}
   */
  #pinToBottomRafId = null;

  /**
   * Clears the `header--row-bottom-instant` class (see #updateScrollState)
   * a short moment after it's applied, so only the collapse that happens
   * to fire while at the bottom skips its animation -- every other
   * collapse elsewhere on the page keeps it.
   * @type {number | null}
   */
  #instantRowBottomTimeoutId = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Whether the scroll container was sitting at its bottom edge the last
   * time #updateScrollState ran, i.e. just before this header's own height
   * potentially changes (see #resizeObserver below).
   * @type {boolean}
   */
  #wasAtBottom = false;

  /**
   * The scroll container's max scroll position at the moment #wasAtBottom
   * was last set true, i.e. the position we want it to end up back at once
   * .header__row--bottom's collapse (and everything it triggers elsewhere,
   * like --header-group-height) finishes settling. #pinToBottomIfWasAtBottom
   * re-asserts *this* remembered value rather than re-reading the
   * container's current max each time: that current max dips mid-settle
   * (the row's own box shrinks a frame or two before whatever reserves
   * space for it elsewhere catches back up), and re-targeting a moving,
   * temporarily-too-small max just re-triggers the same clamp we're trying
   * to avoid. Assigning past the container's current (dipped) max is safe
   * either way -- the browser clamps it right back down to that max on its
   * own, so re-asserting the remembered value is a no-op until the real
   * max recovers, at which point it finally sticks.
   * @type {number}
   */
  #targetBottomScrollTop = 0;

  /**
   * scrollTop as of the last #pinToBottomIfWasAtBottom check, or null right
   * after a poll window (re)starts. Used to tell a genuine user scroll-up
   * gesture (scrollbar drag, swipe) apart from the passive browser clamp
   * this pin is meant to correct: a real gesture moves scrollTop *up*
   * frame-to-frame under its own steam, where the clamp this counters just
   * leaves it sitting still at a too-low value. See
   * #pinToBottomIfWasAtBottom.
   * @type {number | null}
   */
  #lastPolledScrollTop = null;

  /**
   * True while a pointer is pressed anywhere on the page -- crucially,
   * including a drag of the native scrollbar (Chromium fires mousedown /
   * pointerdown for that). The bottom-pin exists to undo the browser's
   * *passive* scrollTop clamp when this sticky header's height changes; it
   * must never fight a deliberate scrollbar drag. The drag's own scroll
   * events land inside the near-bottom zone frame after frame, and the row
   * collapse/expand they trigger keeps #targetBottomScrollTop (and so
   * `isFreshArm`) changing, which restarts #pollPinToBottom and nulls
   * #lastPolledScrollTop every frame -- so the "user is scrolling up"
   * release check never survives long enough to fire, and the pin yanks
   * the user back toward the bottom on every frame (felt as: the scrollbar
   * "snaps" near the bottom and can't be dragged back up). While this is
   * true the pin is fully disarmed; a pointerdown also abandons any pin
   * already in flight.
   * @type {boolean}
   */
  #pointerActive = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    // This header is sticky and stays in the scroll container's normal
    // flow, so its own height directly changes that container's
    // scrollHeight -- e.g. .header__row--bottom collapsing away as
    // #updateScrollState sets data-scroll-direction below. If the user was
    // sitting at the very bottom right before that height change, the
    // container's max scroll position shrinks (or fluctuates) out from
    // under them and the browser clamps scrollTop to whatever the new max
    // happens to be at that instant, with nothing to scroll it back once
    // things settle -- felt as a hard bounce/snap away from the bottom.
    // Re-pinning here catches it as early as possible; #pollPinToBottom
    // (started from #updateScrollState) is what actually carries this
    // through to the end, since further height changes this triggers
    // elsewhere on the page (see #pollPinToBottom's docs) aren't
    // necessarily changes to *this* element that this observer would see.
    this.#pinToBottomIfWasAtBottom();
  });

  /**
   * Re-pins the scroll container to #targetBottomScrollTop (the bottom edge
   * it was sitting at just before this header's height started changing --
   * see #wasAtBottom / #resizeObserver above) if it was sitting there.
   */
  #pinToBottomIfWasAtBottom() {
    if (!this.#wasAtBottom || this.#pointerActive) return;

    const container = getScrollContainer();
    const currentScrollTop = container.scrollTop;

    // A genuine user scroll-up gesture (scrollbar drag, swipe) moves
    // scrollTop upward under its own steam, frame over frame, while this is
    // still polling because it's within the near-bottom zone. The passive
    // clamp this pin is meant to correct doesn't do that -- it lands once
    // and then sits still (however wrong) until we correct it. So a
    // same-direction upward move since the last check means the user is
    // actively scrolling away right now: release immediately instead of
    // yanking them back down for the rest of this poll window.
    if (this.#lastPolledScrollTop !== null && currentScrollTop < this.#lastPolledScrollTop) {
      this.#wasAtBottom = false;
      this.#lastPolledScrollTop = null;
      return;
    }

    this.#lastPolledScrollTop = currentScrollTop;

    if (currentScrollTop !== this.#targetBottomScrollTop) {
      container.scrollTo({ top: this.#targetBottomScrollTop, behavior: 'instant' });
      this.#lastPolledScrollTop = this.#targetBottomScrollTop;
    }
  }

  /**
   * Calls #pinToBottomIfWasAtBottom on every frame until `deadline`.
   * @param {number} deadline - A performance.now() timestamp.
   */
  #pollPinToBottom = (deadline) => {
    if (this.#pinToBottomRafId !== null) cancelAnimationFrame(this.#pinToBottomRafId);
    // Fresh poll window -- don't let a scrollTop reading left over from a
    // previous window (or from before this one's first frame) be misread
    // as "the user just scrolled up since last check".
    this.#lastPolledScrollTop = null;

    const step = () => {
      this.#pinToBottomIfWasAtBottom();
      this.#pinToBottomRafId = performance.now() < deadline ? requestAnimationFrame(step) : null;
    };
    this.#pinToBottomRafId = requestAnimationFrame(step);
  };

  /**
   * A pointer went down anywhere (incl. a native scrollbar drag). Disarm
   * the bottom-pin and kill any poll window already running so a
   * deliberate drag is never fought. See #pointerActive.
   */
  #handlePointerDown = () => {
    this.#pointerActive = true;
    this.#wasAtBottom = false;
    this.#lastPolledScrollTop = null;
    if (this.#pinToBottomRafId !== null) {
      cancelAnimationFrame(this.#pinToBottomRafId);
      this.#pinToBottomRafId = null;
    }
  };

  /** Pointer released -- let the pin arm again on subsequent height changes. */
  #handlePointerUp = () => {
    this.#pointerActive = false;
  };

  /**
   * Whether this header occupies space in the scroll container's flow, i.e.
   * its height can change the container's scrollHeight -- the only case the
   * bottom-pin exists for. False when the header (or its section wrapper)
   * is position: fixed: mobile, and the homepage overlay header.
   * @returns {boolean}
   */
  #isInScrollFlow() {
    if (getComputedStyle(this).position === 'fixed') return false;
    const section = this.closest('.header-section');
    return !section || getComputedStyle(section).position !== 'fixed';
  }

  /**
   * @param {TransitionEvent} event
   */
  #handleRowBottomTransitionEnd = (event) => {
    if (event.propertyName !== 'max-height') return;
    this.#pinToBottomIfWasAtBottom();
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    // Snapshot this before the scroll-direction change below can trigger
    // .header__row--bottom's collapse, which #resizeObserver uses to decide
    // whether to keep the container pinned to the bottom through that
    // height change. The threshold is intentionally loose -- comfortably
    // more than the row's own 50px collapsed height, not just a couple px:
    // that collapse is now instant (see `header--row-bottom-instant`
    // below), so the browser's own clamp can move scrollTop the full 50px
    // in a single native `scroll` event before our own correction below
    // gets a chance to run. A tight threshold here would misread that
    // native clamp itself as "the user scrolled away from the bottom on
    // purpose" and give up on the fix for that cycle.
    const container = getScrollContainer();
    const newTargetBottomScrollTop = container.scrollHeight - container.clientHeight;
    const wasAlreadyAtBottom = this.#wasAtBottom;
    // Never arm the pin while the user is actively dragging (scrollbar,
    // touch) -- see #pointerActive. A deliberate drag must win outright.
    //
    // Nor when the header is out of the scroll flow (position: fixed --
    // all of mobile <990px, and the homepage overlay header): its height
    // can't change the page's scrollHeight there, so there is nothing for
    // the pin to correct. On mobile it was actively harmful (Dylan,
    // 2026-10-20: "it gets locked at the bottom"): scrolling up brings the
    // browser's address bar back, which changes clientHeight -> a "new"
    // bottom target -> a fresh arm -> the 500ms poll yanked the page back
    // to the bottom on every attempt.
    this.#wasAtBottom =
      this.#isInScrollFlow() && !this.#pointerActive && newTargetBottomScrollTop - scrollTop <= 60;

    if (this.#wasAtBottom) {
      // Only treat this as a fresh arm -- and restart the poll window --
      // when we just entered the near-bottom zone or the container's real
      // max scroll position actually changed (i.e. a height change like
      // .header__row--bottom's collapse genuinely happened). Every other
      // scroll event while already sitting in the zone (e.g. a continuous
      // scrollbar drag) leaves the already-running #pollPinToBottom loop
      // alone -- restarting it here would null out #lastPolledScrollTop
      // (see #pollPinToBottom) and erase the frame-to-frame history that
      // loop needs to tell a genuine upward drag apart from the passive
      // clamp, making it snap the user back down every time instead of
      // ever releasing.
      const isFreshArm = !wasAlreadyAtBottom || newTargetBottomScrollTop !== this.#targetBottomScrollTop;
      this.#targetBottomScrollTop = newTargetBottomScrollTop;

      if (isFreshArm) {
        // Make the collapse this triggers below instant rather than
        // animated -- see the `header--row-bottom-instant` rule in
        // theme.liquid. Re-pinning scrollTop while that collapse is
        // *animating* still reads as a visible jiggle even once it settles
        // in the right place, because the target it's chasing keeps moving
        // for the whole 0.3s. Skipping the animation removes the moving
        // target instead of chasing it. This has to be added before
        // dataset.scrollDirection changes below, in the same synchronous
        // task, or the transition has already started by the time it takes
        // effect.
        this.classList.add('header--row-bottom-instant');
        if (this.#instantRowBottomTimeoutId) clearTimeout(this.#instantRowBottomTimeoutId);
        this.#instantRowBottomTimeoutId = setTimeout(() => {
          this.#instantRowBottomTimeoutId = null;
          this.classList.remove('header--row-bottom-instant');
        }, 100);

        this.#pollPinToBottom(performance.now() + 500);
      }
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      // Uses the actual scroll position here instead of the shared
      // `isAtTop` (which reads this element's own rendered rect) --
      // `headerTop` is meaningless for "always" mode specifically on
      // mobile, where this element is `position: fixed` (see the mobile
      // media query in sections/header.liquid) so its own rect.top never
      // moves at all, staying permanently >= 0 regardless of real scroll
      // position. That would misread every scroll as "still at the top"
      // and the row below would never collapse.
      const isPageAtTop = scrollTop <= 0;
      if (isPageAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('transitionend', this.#handleRowBottomTransitionEnd);
    // Capture-phase + passive: a native scrollbar drag fires these on the
    // window and must disarm the bottom-pin before its scroll events land.
    window.addEventListener('pointerdown', this.#handlePointerDown, { capture: true, passive: true });
    window.addEventListener('mousedown', this.#handlePointerDown, { capture: true, passive: true });
    window.addEventListener('pointerup', this.#handlePointerUp, { capture: true, passive: true });
    window.addEventListener('mouseup', this.#handlePointerUp, { capture: true, passive: true });
    window.addEventListener('pointercancel', this.#handlePointerUp, { capture: true, passive: true });

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('transitionend', this.#handleRowBottomTransitionEnd);
    window.removeEventListener('pointerdown', this.#handlePointerDown, { capture: true });
    window.removeEventListener('mousedown', this.#handlePointerDown, { capture: true });
    window.removeEventListener('pointerup', this.#handlePointerUp, { capture: true });
    window.removeEventListener('mouseup', this.#handlePointerUp, { capture: true });
    window.removeEventListener('pointercancel', this.#handlePointerUp, { capture: true });
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#pinToBottomRafId !== null) {
      cancelAnimationFrame(this.#pinToBottomRafId);
      this.#pinToBottomRafId = null;
    }
    if (this.#instantRowBottomTimeoutId) {
      clearTimeout(this.#instantRowBottomTimeoutId);
      this.#instantRowBottomTimeoutId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
